import { FC, useCallback, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import { compact, startCase, uniqBy } from 'lodash';
import {
  useDeleteAttachment,
  useDeleteNote as useDeleteComment,
  useUpdateNote as useUpdateComment,
} from 'modules/account/contact/hooks';
import { BiDotsVerticalRounded } from 'react-icons/bi';
import { useLocation } from 'react-router-dom';
import NoteCommentForm from 'ui/components/common/Entry/NotesEntry/NoteCommentForm';
import { Avatar } from 'ui/components/img';
import { NavAction } from 'ui/components/nav';
import c, { SIZE } from 'utils/constants';
import {
  addClassNameToRichText,
  convertToMegabytes,
  downloadURI,
  useUploadToUploadCare,
} from 'utils/helpers';
import { useLeadNotesContext } from 'modules/account/contact/containers/Leads/Notes/Notes';
import { useCompanyNotesContext } from 'modules/account/contact/containers/Companies/Notes/Notes';
import { useTasksContext } from 'modules/account/sales/containers/Tasks';
import { useDealsContext } from 'modules/account/sales/containers/Deals/Deals';
import {
  useGetProfile,
  useGetSubscription,
} from 'modules/account/settings/hooks';
import { SupportedFileInput } from '@uploadcare/upload-client';
import type { FileRejection } from 'react-dropzone';
import useModal from 'ui/partials/Modal/useModal';
import { Comment as IComment, User } from 'utils/types';
import {
  AttachedItemsList,
  ViewAllAttachmentsModal,
} from 'modules/account/contact/components';
import { useAppWrapperContext } from 'ui/layouts/AppWrapper';

import AttachmentsTooLargeModal from '../AttachmentTooLarge';
import DeleteItemModal from '../DeleteItemModal';

export interface Attachment {
  size: number;
  name: string;
  cdnUrl: string;
  mimeType: string;
  originalFilename: string;
  type: string;
}

interface PROPS {
  comment: IComment;
  user?: User;
  maxSize?: number;
  referenceId?: string;
}

const useNote = () => ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setSelectedNoteId: (_id: string | null) => {},
  selectedNoteId: '',
});

const Comment: FC<PROPS> = ({
  comment,
  user,
  maxSize = c.MAX_UPLOAD_SIZE,
  referenceId,
}) => {
  const { data: subscription } = useGetSubscription();
  const { data: profile } = useGetProfile();
  const { pathname } = useLocation();

  //TODO: Type this properly
  const useNoteId: any = useMemo(() => {
    if (pathname.includes('leads')) {
      return useLeadNotesContext;
    }

    if (pathname.includes('company')) {
      return useCompanyNotesContext;
    }

    if (pathname.includes('task')) {
      return useTasksContext;
    }

    if (pathname.includes('deal')) {
      return useDealsContext;
    }

    return useNote;
  }, [pathname]);

  const note = useNoteId();
  const { showModal } = useModal();
  const {
    state: { isPreview },
  } = useAppWrapperContext();

  const [value, setValue] = useState(comment?.content);
  const [editing, setEditing] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const { mutate: deleteAttachment, isLoading: deleteAttachmentLoading } =
    useDeleteAttachment();
  const { mutateAsync: uploadFiles, isLoading } = useUploadToUploadCare();
  const { mutate: editComment } = useUpdateComment();
  const { mutate: deleteComment, isLoading: deleteLoading } =
    useDeleteComment();

  const totalAttachmentSize = useMemo(
    () => attachments?.reduce((acc, curr) => acc + curr.size, 0),
    [attachments],
  );

  const noOfAttachments = comment?.attachments?.length;

  const toggleAttachments = () => setShowAttachments((prevVal) => !prevVal);

  const handleDeleteAttachment = useCallback(
    (attachmentId: string) => {
      deleteAttachment({
        id: attachmentId,
        isComment: true,
        noteId: comment?.parentId,
        referenceId,
      });
    },
    [comment, deleteAttachment, referenceId],
  );

  const showCommentDeleteModal = (
    id: string,
    noteId: string,
    refId: string,
    parentId?: string,
  ) => {
    showModal({
      title: 'Comment',
      content: (
        <DeleteItemModal
          text="comment"
          onClickDelete={() =>
            deleteComment({ id, noteId, parentId, referenceId: refId })
          }
          isLoading={deleteLoading}
          validation
        />
      ),
      disableOutsideClick: true,
    });
  };

  const showAttachmentDeleteModal = useCallback(
    (attachmentId: string) => {
      showModal({
        title: 'Attachment',
        content: (
          <DeleteItemModal
            text="attachment"
            onClickDelete={() => handleDeleteAttachment(attachmentId)}
            isLoading={deleteAttachmentLoading}
            validation
          />
        ),
        disableOutsideClick: true,
      });
    },
    [handleDeleteAttachment, showModal, deleteAttachmentLoading],
  );

  const showAttachmentsTooLargeModal = (fileSize: string) => {
    showModal({
      title: 'Attachment',
      content: (
        <AttachmentsTooLargeModal fileSize={fileSize} maxSize={maxSize} />
      ),
      disableOutsideClick: true,
    });
  };

  const isNoteOwner = useMemo(() => {
    if (isPreview) return comment?.actor?.actorId === profile?._id;

    return profile?._id === comment?.userId?._id;
  }, [comment, profile, isPreview]);

  const navOptions = compact([
    isNoteOwner && {
      text: 'Edit',
      key: 'edit',
      onClick: () => {
        note?.setSelectedNoteId?.(comment?.id);
        setEditing(true);
      },
    },
    isNoteOwner && {
      text: 'Delete',
      key: 'delete',
      onClick: () =>
        showCommentDeleteModal(
          comment.id,
          comment.parentId!,
          comment.referenceId,
          comment.parentId,
        ),
    },
  ]);

  const handleSave = async () => {
    const uploadedAttachments =
      attachments.length &&
      (await uploadFiles(attachments as unknown as SupportedFileInput[]));

    editComment(
      {
        id: comment.id,
        payload: {
          content: value,
          ...(uploadedAttachments && {
            attachments: uploadedAttachments.files.map((file) => ({
              fileUrl: file.cdnUrl ?? '',
              fileType: file.mimeType ?? '',
              fileName: file.originalFilename ?? '',
              fileSize: file.size ?? 0,
              source: startCase(comment.source),
              ...(file.isImage && { fileThumbnail: file.cdnUrl ?? '' }),
            })),
          }),
        },
        referenceId: comment.referenceId,
        parentId: comment.parentId,
      },
      {
        onSuccess: () => {
          setAttachments([]);
          setEditing(attachments.length ? !uploadedAttachments : false);
          note?.setSelectedNoteId?.(null);
        },
      },
    );
  };

  const handleCancel = () => {
    setValue(comment.content);
    setAttachments([]);
    note?.setSelectedNoteId?.(null);
    setEditing(false);
  };

  const removeAttachmentFromForm = (name: string) => {
    setAttachments((prevVal) => prevVal.filter((val) => !(val.name === name)));
  };

  const isEdited = comment.createdAt !== comment.updatedAt;

  const onDrop = <T extends File>(_acceptedFiles: T[]) => {
    setAttachments(
      (prevVal) =>
        uniqBy([...prevVal, ..._acceptedFiles], 'name') as Attachment[],
    );
    const sizeUploaded =
      comment.attachments?.reduce((acc, curr) => acc + curr.fileSize, 0) ?? 0;
    const sizeToUpload: number = [...attachments, ..._acceptedFiles].reduce(
      (acc, curr) => acc + curr.size,
      0,
    );
    if (sizeUploaded + sizeToUpload > maxSize) {
      showAttachmentsTooLargeModal(
        convertToMegabytes(sizeUploaded + sizeToUpload),
      );
    }
  };

  const onDropRejected = (fileRejections: FileRejection[]) => {
    const _totalSize: number = fileRejections.reduce(
      (acc, { errors, file }) => {
        if (errors[0].code === 'file-too-large') {
          return acc + file.size;
        }
        return acc;
      },
      0,
    );
    if (fileRejections[0].errors[0].code === 'file-too-large') {
      showAttachmentsTooLargeModal(convertToMegabytes(_totalSize));
    }
  };

  const showAllAttachmentsModal = () => {
    showModal({
      content: (
        <ViewAllAttachmentsModal
          referenceId={comment._id}
          source={'Comment'}
          onPreviewClose={showAllAttachmentsModal}
        />
      ),
      title: 'Attachments',
      size: SIZE.Large,
    });
  };

  const avatarUser = useMemo(() => {
    if (!user) {
      const _user = comment?.actor?.meta as string;

      if (_user) return JSON.parse(_user);

      return null;
    }

    return user;
  }, [user, comment]);
  const fullName = useMemo(() => {
    if (!user) {
      const _user = comment?.actor?.meta as string;

      if (_user) return JSON.parse(_user)?.fullName;

      return null;
    }

    return user?.fullName;
  }, [user, comment]);

  return (
    <div className="space-y-[10px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[14px]">
          {!avatarUser ? null : (
            <Avatar
              className="sm"
              size={SIZE.Small}
              user={avatarUser}
              name={user?.photoUrl}
            />
          )}
          <div>
            <div className="text-sm text-secondary">{fullName}</div>
            <div className="flex items-center gap-1 text-xs text-content">
              <div>
                {dayjs(comment?.updatedAt).format('MMM DD YYYY hh:mm a')}
              </div>
              {isEdited && <div>(Updated)</div>}
            </div>
          </div>
        </div>
        {isNoteOwner && !subscription?.isExpired && (
          <NavAction
            label={<BiDotsVerticalRounded className="h-5 w-5" />}
            options={navOptions}
          />
        )}
      </div>
      {!editing ? (
        <div
          className="break-all"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              addClassNameToRichText('text-sm', comment?.content) as string,
            ),
          }}
        />
      ) : (
        <NoteCommentForm
          name="edit-comment"
          placeholder="Edit Comment"
          onChange={(e) => setValue(e)}
          attachments={attachments}
          onRemoveAttachment={removeAttachmentFromForm}
          disabled={!value || totalAttachmentSize > maxSize}
          onCancel={handleCancel}
          onSave={handleSave}
          onDrop={onDrop}
          onDropRejected={onDropRejected}
          value={value}
          isLoading={isLoading}
        />
      )}
      {!!noOfAttachments && (
        <button
          onClick={toggleAttachments}
          type="button"
          className="pr-3 text-xs font-semibold hover:text-primary"
        >
          {`${noOfAttachments} Attachments`}
        </button>
      )}
      {showAttachments && comment?.attachments && (
        <AttachedItemsList
          hideNumberOfFilesUploaded
          attachments={comment.attachments}
          onDownload={downloadURI}
          showRemove={editing}
          onRemove={showAttachmentDeleteModal}
          onClickShowAllAttachments={showAllAttachmentsModal}
        />
      )}
    </div>
  );
};

export default Comment;
